import { useTranslation } from 'react-i18next';

import AssetDetail from '@/components/Assets/AssetDetails/AssetDetail';
import { Asset } from '@/types/assets';
import { Divider, spaces } from '@electricjs/arc';
import { DetailsContainer } from '@/components/Assets/AssetDetails/common';
import { useGetSiteLocationsQuery } from '@/redux/slices/assetApiSlice';
import { getAssetConditionDisplayNameMap } from '@/components/Assets/helpers/transformOptions';
import { getOsFullName } from 'common/utils/getOsFullName';
import { DetailSection } from '@/pages/Assets/AssetDetailsContent/DetailSection';
import { AssetDetailsBaseCard } from '@/pages/Assets/AssetDetailsContent/AssetDetailsBaseCard';

const AssetDetailItem = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  return (
    <AssetDetail
      label={label}
      labelVariant="label-small"
      value={value}
      valueVariant="body-2"
    />
  );
};

const AssetDetailsCardV2 = ({ asset }: { asset: Asset }) => {
  const { t } = useTranslation('assets');
  const siteId = asset?.site?.id ?? '';
  const { space_s, space_l } = spaces;

  const { locationName } = useGetSiteLocationsQuery(siteId, {
    skip: !siteId,
    selectFromResult: ({ data }) => ({
      locationName: data?.find(location => location.id === asset?.locationId)
        ?.name,
    }),
  });
  const assetConditionDisplayNameMap = getAssetConditionDisplayNameMap(t);
  const assetCondition = asset?.condition
    ? assetConditionDisplayNameMap[asset?.condition]
    : 'N/A';

  const deviceReportingData = asset?.device?.reportingData;

  const hardwareModel = asset.device?.hardwareModel || asset.model;

  return (
    <AssetDetailsBaseCard
      title={t('assetDetailsContent.assetInformation')}
      icon="toolbox">
      <DetailsContainer mt={space_l} mb={space_s} height="550px">
        <DetailSection
          icon="address-card"
          label={t('assetDetailsContent.identification')}>
          <AssetDetailItem
            label={t('assetDetailsContent.assetId')}
            value={asset?.customAssetId || 'N/A'}
          />
          {hardwareModel && (
            <AssetDetailItem
              label={t('assetDetailsContent.hardware')}
              value={hardwareModel}
            />
          )}
          <AssetDetailItem
            label={t('assetDetailsContent.condition')}
            value={assetCondition}
          />
        </DetailSection>
        <Divider />
        <DetailSection icon="pin" label={t('assetDetailsContent.location')}>
          <AssetDetailItem
            label={t('assetDetailsContent.site')}
            value={asset?.site?.name || 'N/A'}
          />
          {locationName && (
            <AssetDetailItem
              label={t('assetDetailsContent.location')}
              value={locationName}
            />
          )}
        </DetailSection>
        <Divider />
        <DetailSection
          icon="list-check"
          label={t('assetDetailsContent.specifications')}>
          <AssetDetailItem
            label={t('assetDetailsContent.operatingSystem')}
            value={
              getOsFullName({
                osName: deviceReportingData?.osName,
                osVersion: deviceReportingData?.osVersion,
                osType: asset?.device?.osType,
              }) ?? 'N/A'
            }
          />
          <AssetDetailItem
            label={t('assetDetailsContent.ram')}
            value={
              deviceReportingData?.ramCapacity
                ? `${deviceReportingData?.ramCapacity} GB`
                : 'N/A'
            }
          />
          <AssetDetailItem
            label={t('assetDetailsContent.hardDriveStorage')}
            value={
              deviceReportingData?.storageCapacity
                ? `${Math.floor(deviceReportingData?.storageCapacity)} GB`
                : 'N/A'
            }
          />
        </DetailSection>
      </DetailsContainer>
    </AssetDetailsBaseCard>
  );
};

export default AssetDetailsCardV2;
